import React, { useEffect } from 'react';
import { useMobile } from '../../hooks/useMobile';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * ResponsiveModal - Adapts to screen size
 * - Full screen on mobile devices
 * - Centered modal on desktop
 * - Touch-friendly close interactions
 */
const ResponsiveModal = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  size = 'md',
  showCloseButton = true,
  closeOnBackdrop = true,
  className = ''
}) => {
  const { isMobile, isTablet } = useMobile();

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Size classes for desktop modal
  const sizeClasses = {
    sm: 'sm:max-w-sm',
    md: 'sm:max-w-lg',
    lg: 'sm:max-w-2xl',
    xl: 'sm:max-w-4xl',
    full: 'sm:max-w-full sm:m-4'
  };

  // Mobile: full screen
  // Tablet/Desktop: centered modal
  const modalClasses = isMobile
    ? 'fixed inset-0 w-full h-full'
    : `fixed inset-0 z-50 flex items-center justify-center p-4`;

  const contentClasses = isMobile
    ? 'w-full h-full bg-white flex flex-col'
    : `bg-white rounded-lg shadow-xl w-full ${sizeClasses[size]} max-h-[90vh] flex flex-col`;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className={modalClasses}>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={`fixed inset-0 bg-black ${isMobile ? 'bg-opacity-0' : 'bg-opacity-50'} z-40`}
            onClick={closeOnBackdrop ? onClose : undefined}
          />

          {/* Modal Content */}
          <motion.div
            initial={isMobile ? { y: '100%' } : { opacity: 0, scale: 0.95 }}
            animate={isMobile ? { y: 0 } : { opacity: 1, scale: 1 }}
            exit={isMobile ? { y: '100%' } : { opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className={`relative z-50 ${contentClasses} ${className}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            {(title || showCloseButton) && (
              <div className={`flex items-center justify-between p-4 border-b border-gray-200 ${isMobile ? 'bg-white shadow-sm' : ''}`}>
                {/* Mobile: Back arrow, Desktop: Title */}
                <div className="flex items-center">
                  {isMobile && (
                    <button
                      onClick={onClose}
                      className="mr-3 p-2 -ml-2 rounded-full hover:bg-gray-100 transition-colors"
                      aria-label="Go back"
                    >
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                  )}
                  {title && (
                    <h2 className={`font-semibold text-gray-900 ${isMobile ? 'text-lg' : 'text-xl'}`}>
                      {title}
                    </h2>
                  )}
                </div>

                {/* Desktop: Close X button */}
                {showCloseButton && !isMobile && (
                  <button
                    onClick={onClose}
                    className="p-2 -mr-2 rounded-full hover:bg-gray-100 transition-colors"
                    aria-label="Close modal"
                  >
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            )}

            {/* Content */}
            <div className={`flex-1 overflow-y-auto ${isMobile ? 'pb-safe' : ''}`}>
              {children}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ResponsiveModal;